import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Landmark, Globe, BarChart3, Shield, MessageSquare, FileText, Leaf, CheckCircle, Users, Lock, Settings, ClipboardList, Eye, Zap, LayoutDashboard, Building2, TrendingUp, Bell } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageCSS = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - CSS (siège) | Gouvernance & Politique"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="css" lang="fr" />

      {/* Hero CSS */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Landmark size={16} />
            <span>Portail CSS (Centre de Services Scolaires)</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-css)' }}>CSS (siège)</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--actor-css)' }}>Gouvernance alimentaire globale</strong><br />
            Politique stratégique et pilotage centralisé
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">360°</div>
              <div className="stat-label">Vue globale</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Traçabilité</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">BI</div>
              <div className="stat-label">Analytics intégrés</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste */}
      <section className="section" data-testid="manifeste-css">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch pour les CSS ?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-css-centralisee">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#E74C3C'}}><Globe size={30} /></div>
              <h3>Centralisation stratégique de la gouvernance alimentaire</h3>
            </div>
            <p>Les Centres de Services Scolaires supervisent des dizaines d'écoles, de directions et de traiteurs avec des outils souvent fragmentés. EcoLunch offre un <strong>portail de gouvernance unifié</strong> qui consolide toutes les données de performance, conformité et durabilité alimentaire en temps réel.</p>
            <p>Un seul tableau de bord exécutif pour piloter l'ensemble de votre réseau, définir les politiques alimentaires, et <strong>prendre des décisions stratégiques éclairées</strong> avec des données fiables et consolidées.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-css-souverainete">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Lock size={30} /></div>
              <h3>Souveraineté des données et conformité garantie</h3>
            </div>
            <p>Toutes les données de votre réseau scolaire sont hébergées au Québec et au Canada par <strong>OVH Cloud</strong> (Montréal & Toronto). Aucune donnée n'est transférée à l'étranger.</p>
            <p>Les standards nutritionnels et les politiques sont définis au niveau du CSS et vérifiés automatiquement pour chaque établissement, assurant une <strong>conformité totale et traçable</strong> lors des audits.</p>
          </div>
        </div>
      </section>

      {/* Centre de Gouvernance */}
      <section id="gouvernance" className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Centre de Gouvernance</h2>
            <p className="section-subtitle">
              Centralisation complète de la gouvernance alimentaire scolaire
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Globe size={32} /></div>
              <h3>Vue Globale</h3>
              <p>Toutes les directions, toutes les écoles, tous les traiteurs. Vision 360° de votre réseau alimentaire complet.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Comparatifs</h3>
              <p>Performance, qualité, conformité. Analysez et comparez chaque établissement de votre réseau.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Gouvernance</h3>
              <p>Indicateurs stratégiques, alertes critiques, tableaux de bord. Pilotez avec précision.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication</h3>
              <p>Politiques, directives, normes. Diffusez vos orientations à l'ensemble du réseau.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Rapports</h3>
              <p>Mensuels, trimestriels, annuels. Personnalisables et automatisables pour vos comités.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Leaf size={32} /></div>
              <h3>Durabilité</h3>
              <p>Menus santé, réduction du gaspillage, produits locaux. Suivez vos engagements environnementaux.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gouvernance Flexible */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Gouvernance Flexible</h2>
            <p className="section-subtitle">
              Un contrôle adapté aux besoins de votre organisation
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Contrôle total</h3>
              <p>Toutes les permissions et accès sous votre contrôle. Définissez qui voit quoi et qui fait quoi.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication directe</h3>
              <p>Avec traiteurs directement ou via les directions d'école. Choisissez le canal approprié.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Délégation flexible</h3>
              <p>Déléguez aux directions d'école selon les besoins. Gardez le contrôle tout en responsabilisant.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Règles paramétrables</h3>
              <p>Escalade, alertes et rapports configurables. Adaptez le système à votre gouvernance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Journalisation */}
      <section className="section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Journalisation</h2>
            <p className="section-subtitle">
              Traçabilité complète et transparence garantie
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <ClipboardList size={40} />
                <h3>Actions traçables</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Toutes les actions effectuées dans le système sont enregistrées et traçables. Qui a fait quoi, quand et pourquoi.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <FileText size={40} />
                <h3>Audit complet</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Lecture, modification, communication : chaque interaction est journalisée. Prêt pour tout audit interne ou externe.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <CheckCircle size={40} />
                <h3>Conformité garantie</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Conformité et transparence garanties à chaque niveau de votre organisation. Documentation automatique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interface Frontend Web */}
      <section className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Interface Frontend Web</h2>
            <p className="section-subtitle">
              Un portail web dédié pour les administrateurs du CSS
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><LayoutDashboard size={32} /></div>
              <h3>Dashboard exécutif temps réel</h3>
              <p>Vue d'ensemble instantanée de la performance de votre réseau. KPIs, tendances et alertes en un coup d'oeil.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Building2 size={32} /></div>
              <h3>Gestion multi-écoles & directions</h3>
              <p>Naviguez facilement entre vos établissements. Comparez, analysez et prenez des décisions éclairées.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Rapports consolidés & KPIs</h3>
              <p>Rapports automatiques personnalisables. Exportez en un clic pour vos comités et conseils d'administration.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Configuration des politiques</h3>
              <p>Définissez vos règles nutritionnelles, budgétaires et opérationnelles depuis une interface centralisée.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Analytics & Business Intelligence</h3>
              <p>Analyses avancées, prévisions et modélisation. Transformez vos données en intelligence décisionnelle.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Alertes & Notifications</h3>
              <p>Soyez informé en temps réel des anomalies, non-conformités ou situations nécessitant votre attention.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-css">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(231, 76, 60, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>pour les CSS</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  Une phrase suffit pour tout piloter
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Performance globale toutes écoles ce trimestre</div>
              <div className="ecopulse-mini-cmd">Compare conformité nutritionnelle par direction</div>
              <div className="ecopulse-mini-cmd">Rapport annuel consolidé pour le conseil</div>
              <div className="ecopulse-mini-cmd">Alertes critiques réseau cette semaine</div>
            </div>
            <button onClick={() => navigate('/ecopulse')} className="ecopulse-mini-link">
              Découvrir toutes les commandes EcoPulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>&copy; 2025 EcoLunch. Tous droits réservés.</span>
              <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontWeight: 600, textDecoration: 'none'}}>ecotechsysteme.ca</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCSS;
